import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  ChevronRight,
  ChevronDown,
  ArrowLeft,
  ArrowUp,
  ArrowDown,
  Info,
  Sparkles,
  AlertCircle,
  Loader2,
  Snowflake,
  Zap,
} from 'lucide-react';
import { ROUTE } from '@/config';
import { cn } from '@/lib/utils';
import { Checkbox } from '@/components/ui/checkbox';
import OrderStepper from '@/components/OrderStepper/OrderStepper';
import useNewOrderStore, { SelectedCarrier } from '@/stores/useNewOrderStore';
import { useTranslation } from 'react-i18next';
import { AvailableCarrierItem } from '@/types';
import { useGetAvailableCarriers, useListShippingRules } from '@/hooksApi';
import { buildAvailableCarriersRequest, processCarrierPreviewResponse } from '@/utils';
import { buildAutoDispatchBannerText } from '@/utils/buildAutoDispatchBannerText';
import { useFetchPickupAddresses } from '@/hooksApi/usePickupAddress';
import { formatTimeInIraqTimezone } from '@/utils/helpers/dateHelper';

type SortOption = 'price' | 'price-desc' | 'date' | 'date-desc' | 'name' | 'name-desc';

const titleCase = (s: string) =>
  s
    .split(' ')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');

const SelectCarrier = () => {
  const { t, i18n } = useTranslation();

  // Translate carrier name using slug key, fallback to API name
  const carrierDisplayName = (slug: string, apiName: string) => {
    const translated = String(t(slug));
    return translated !== slug ? translated : titleCase(apiName);
  };
  const isRTL = i18n.dir() === 'rtl';
  const navigate = useNavigate();
  const {
    selectedCarrier,
    setSelectedCarrier,
    orderPayload,
    carrierSelection,
    setCarrierSelection,
    sourceRegionUid,
    ruleDispatch,
    isEditMode,
    editOrderUid,
  } = useNewOrderStore();

  // RS-23 — auto-dispatch banner. Lookup the rule name from the merchant's
  // current rule set so the banner can render "by rule X". The rules query
  // is cached aggressively (React Query) so this is effectively free; we
  // only fire it when there's a dispatch to display.
  const { data: rulesResp } = useListShippingRules(!!ruleDispatch);
  const dispatchRuleName = useMemo(() => {
    if (!ruleDispatch?.winningRuleUid) return undefined;
    return rulesResp?.object?.find((r) => r.uid === ruleDispatch.winningRuleUid)?.name;
  }, [ruleDispatch, rulesResp]);

  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [sortBy, setSortBy] = useState<SortOption>('price');
  const [selectedCouriers, setSelectedCouriers] = useState<Set<string>>(new Set());
  const [isScrolledUp, setIsScrolledUp] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  const { mutate: fetchCarrierPreview } = useGetAvailableCarriers();
  const { data: pickupAddressesData } = useFetchPickupAddresses();

  // Guard: redirect to step 1 if no order payload
  useEffect(() => {
    if (!orderPayload) {
      navigate(isEditMode && editOrderUid ? `/orders/edit/${editOrderUid}` : ROUTE.ADDNEWORDER, { replace: true });
    }
  }, [orderPayload, navigate, isEditMode, editOrderUid]);

  // Fetch carriers if we don't have selection data yet (e.g., direct navigation or refresh)
  useEffect(() => {
    if (orderPayload && !carrierSelection && sourceRegionUid) {
      setIsLoading(true);
      setError(null);
      const carrierRequest = buildAvailableCarriersRequest(orderPayload, sourceRegionUid);
      fetchCarrierPreview(carrierRequest, {
        onSuccess: (response) => {
          const rawCarriers = response.raw_available_carriers;
          if (rawCarriers) {
            setCarrierSelection({
              process_id: response.process_id,
              recommended_carrier_slug: response.recommended_carrier_slug,
              available_carriers: rawCarriers,
            });
          } else {
            // Fallback: reconstruct from adapted response
            const allCarriers = processCarrierPreviewResponse(response);
            const available: AvailableCarrierItem[] = allCarriers.map((c) => ({
              carrier_slug: c.carrier.slug || '',
              carrier_name: c.carrier.title,
              carrier_logo_url: c.carrier.avatar?.abs_url || '',
              shipping_fee: parseFloat(c.shipping_fee) || 0,
              delivery_day: c.serviceType === 'same_day' ? 'same' as const : 'next' as const,
              same_day_cutoff: c.serviceType === 'same_day' ? c.if_ship_before : null,
              drop_off_time: c.serviceType === 'next_day' ? c.if_ship_before : null,
              // TEMP-9: keep the cold-chain capability through the fallback
              // reconstruction (undefined when the backend hasn't sent it).
              supports_temperature_control: c.supports_temperature_control,
            }));
            setCarrierSelection({
              process_id: response.process_id,
              recommended_carrier_slug: response.recommended_carrier_slug,
              available_carriers: available,
            });
          }
          setIsLoading(false);
        },
        onError: () => {
          setError(String(t('no_active_carrier_available')));
          setIsLoading(false);
        },
      });
    }
  }, [orderPayload, carrierSelection, sourceRegionUid]);

  // Scroll shadow detection
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const handleScroll = () => {
      const isAtBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 20;
      setIsScrolledUp(!isAtBottom);
    };
    handleScroll();
    el.addEventListener('scroll', handleScroll);
    return () => el.removeEventListener('scroll', handleScroll);
  }, []);

  const carriers = carrierSelection?.available_carriers ?? [];
  const recommendedSlug = carrierSelection?.recommended_carrier_slug;

  // Get unique courier names for the sidebar filter
  const uniqueCouriers = useMemo(() => {
    const names = new Map<string, string>();
    carriers.forEach((c) => {
      // Extract base carrier name (remove "Standard", "Express", "Economy" etc.)
      const baseName = c.carrier_name.split(' ')[0];
      if (!names.has(c.carrier_slug)) {
        names.set(c.carrier_slug, baseName);
      }
    });
    return Array.from(names.entries());
  }, [carriers]);

  // Filter carriers
  const filtered = useMemo(() => {
    if (selectedCouriers.size === 0) return carriers;
    return carriers.filter((c) => selectedCouriers.has(c.carrier_slug));
  }, [carriers, selectedCouriers]);

  // Sort carriers
  const sortFn = (a: AvailableCarrierItem, b: AvailableCarrierItem) => {
    switch (sortBy) {
      case 'price':
        return a.shipping_fee - b.shipping_fee;
      case 'price-desc':
        return b.shipping_fee - a.shipping_fee;
      case 'name':
        return a.carrier_name.localeCompare(b.carrier_name);
      case 'name-desc':
        return b.carrier_name.localeCompare(a.carrier_name);
      case 'date':
        if (a.delivery_day !== b.delivery_day) {
          return a.delivery_day === 'same' ? -1 : 1;
        }
        return a.shipping_fee - b.shipping_fee;
      case 'date-desc':
        if (a.delivery_day !== b.delivery_day) {
          return a.delivery_day === 'same' ? 1 : -1;
        }
        return b.shipping_fee - a.shipping_fee;
      default:
        return 0;
    }
  };

  // Sort all carriers together in a single list
  const sorted = useMemo(
    () => [...filtered].sort(sortFn),
    [filtered, sortBy],
  );

  // Auto-select the recommended carrier when carriers load
  useEffect(() => {
    if (recommendedSlug && sorted.length > 0 && !selectedCarrier) {
      const recommended = sorted.find((c) => c.carrier_slug === recommendedSlug);
      if (recommended) {
        setSelectedCarrier({
          carrier_slug: recommended.carrier_slug,
          carrier_name: recommended.carrier_name,
          carrier_logo_url: recommended.carrier_logo_url,
          shipping_fee: recommended.shipping_fee,
          delivery_day: recommended.delivery_day,
          same_day_cutoff: recommended.same_day_cutoff,
          drop_off_time: recommended.drop_off_time,
        });
      }
    }
  }, [recommendedSlug, sorted, selectedCarrier, setSelectedCarrier]);

  // Compute the shipping date display
  const shippingDate = useMemo(() => {
    const date = new Date();
    return date.toLocaleDateString(isRTL ? 'ar-IQ' : 'en-GB', {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
    });
  }, [isRTL]);

  const commitSelection = (carrier: AvailableCarrierItem) => {
    const selected: SelectedCarrier = {
      carrier_slug: carrier.carrier_slug,
      carrier_name: carrier.carrier_name,
      carrier_logo_url: carrier.carrier_logo_url,
      shipping_fee: carrier.shipping_fee,
      delivery_day: carrier.delivery_day,
      same_day_cutoff: carrier.same_day_cutoff,
      drop_off_time: carrier.drop_off_time,
    };
    setSelectedCarrier(selected);
  };

  const handleSelect = (carrier: AvailableCarrierItem) => {
    // SRE-2: shipping rules are binding — the carrier list is already
    // filtered to the rule's carriers upstream, so any visible carrier is a
    // valid in-rule choice. There is no override path.
    commitSelection(carrier);
  };

  const toggleRow = (slug: string) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(slug)) next.delete(slug);
      else next.add(slug);
      return next;
    });
  };

  const toggleCourierFilter = (slug: string) => {
    setSelectedCouriers((prev) => {
      const next = new Set(prev);
      if (next.has(slug)) next.delete(slug);
      else next.add(slug);
      return next;
    });
  };

  const getDeliveryDateDisplay = (carrier: AvailableCarrierItem) => {
    if (carrier.delivery_day === 'same') {
      const today = new Date();
      return today.toLocaleDateString(isRTL ? 'ar-IQ' : 'en-GB', {
        weekday: 'short',
        day: 'numeric',
        month: 'short',
      });
    }
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return tomorrow.toLocaleDateString(isRTL ? 'ar-IQ' : 'en-GB', {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
    });
  };

  const getDeliveryTypeLabel = (carrier: AvailableCarrierItem) => {
    if (carrier.delivery_day === 'same') {
      return String(t('same_day_delivery', 'Same-day delivery'));
    }
    return String(t('estimated_delivery'));
  };

  const getCutoffMessage = (carrier: AvailableCarrierItem) => {
    if (carrier.delivery_day !== 'same') return null;
    const cutoff = carrier.same_day_cutoff || carrier.drop_off_time;
    if (!cutoff) return null;
    const formattedTime = formatTimeInIraqTimezone(cutoff);
    return String(t('carrier_cutoff_message', 'The order should be scheduled until {{time}} for same-day services.', { time: formattedTime }));
  };

  if (!orderPayload) return null;

  return (
    <div className='sd-scope sd-font relative flex flex-col -mx-4 sm:-mx-8 -mt-6 -mb-6 h-[calc(100dvh-65px)] overflow-hidden'>
      {/* Content */}
      <div ref={scrollRef} className='flex-1 overflow-y-auto px-4 sm:px-8 pt-4 sm:pt-6 pb-28'>
        {/* Breadcrumb */}
        <nav className='flex items-center gap-1.5 text-sm text-sd-muted-foreground mb-6'>
          <Link to={ROUTE.ORDERS} className='text-sd-muted-foreground hover:text-sd-foreground transition-colors'>
            {String(t('orders'))}
          </Link>
          <ChevronRight className={cn('h-3.5 w-3.5', isRTL && 'rotate-180')} strokeWidth={1.5} />
          <Link
            to={isEditMode && editOrderUid ? `/orders/edit/${editOrderUid}` : ROUTE.ADDNEWORDER}
            className='text-sd-muted-foreground hover:text-sd-foreground transition-colors'
          >
            {String(t(isEditMode ? 'edit_order' : 'new_order'))}
          </Link>
          <ChevronRight className={cn('h-3.5 w-3.5', isRTL && 'rotate-180')} strokeWidth={1.5} />
          <span className='text-sd-foreground font-medium'>{String(t('select_carrier'))}</span>
        </nav>

        <OrderStepper currentStep={2} />

        {/* RS-23 — Auto-dispatch transparency banner. Shows when a shipping
            rule auto-selected the carrier OR restricted the carrier list. */}
        {ruleDispatch && selectedCarrier && (() => {
          const banner = buildAutoDispatchBannerText(
            { carrierName: selectedCarrier.carrier_name, ruleName: dispatchRuleName },
            (k, opts) => String(t(k, opts as Record<string, unknown>)),
          );
          return (
            <div className='mt-4 rounded-xl border border-blue-200 bg-blue-50 px-4 py-3 flex items-start gap-3'>
              <Zap className='h-5 w-5 shrink-0 text-blue-600 mt-0.5' strokeWidth={2} />
              <div className='flex-1 min-w-0'>
                <p className='text-sm font-medium text-blue-900'>{banner.title}</p>
              </div>
            </div>
          );
        })()}

        {/* Loading State */}
        {isLoading && (
          <div className='flex flex-col items-center justify-center py-20'>
            <Loader2 className='h-8 w-8 animate-spin text-sd-primary mb-4' />
            <p className='text-sm text-sd-muted-foreground'>{String(t('loading', 'Loading...'))}</p>
          </div>
        )}

        {/* Error State */}
        {error && !isLoading && (
          <div className='flex flex-col items-center justify-center py-20'>
            <AlertCircle className='h-10 w-10 text-sd-muted-foreground mb-4' />
            <p className='text-sm text-sd-muted-foreground mb-4'>{error}</p>
            {recommendedSlug && (
              <div className='rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 max-w-sm text-start'>
                <div className='flex items-center gap-1.5'>
                  <Sparkles className='h-3.5 w-3.5 text-emerald-600' strokeWidth={1.5} />
                  <span className='text-xs font-semibold uppercase tracking-wider text-emerald-600'>
                    {String(t('recommended'))}
                  </span>
                </div>
                <p className='text-sm font-medium text-sd-foreground mt-1'>
                  {String(t('enable_carriers_recommendation', { carrier: recommendedSlug }))}
                </p>
              </div>
            )}
            <Link
              to={ROUTE.SETTINGS_SHIPPING_RULES}
              className='mt-4 h-9 rounded-md border border-sd-border px-3 text-sm font-medium text-sd-foreground hover:bg-sd-muted transition-colors inline-flex items-center'
            >
              {String(t('go_to_carrier_settings'))}
            </Link>
          </div>
        )}

        {/* Main Content */}
        {!isLoading && !error && (
          <div className='grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-6'>
            {/* Left Sidebar Filters */}
            <div className='flex flex-col gap-6'>
              <section className='rounded-2xl border border-sd-border bg-sd-card p-5 shadow-sm'>
                {/* Shipping Date */}
                <div className='mb-5'>
                  <p className='text-xs font-semibold uppercase tracking-wider text-sd-muted-foreground mb-1'>
                    {String(t('shipping_date', 'Shipping Date:'))}
                  </p>
                  <p className='text-sm font-medium text-sd-foreground'>
                    {shippingDate}
                  </p>
                </div>

                {/* Sort By */}
                <div className='mb-5'>
                  <p className='text-xs font-semibold uppercase tracking-wider text-sd-muted-foreground mb-2'>
                    {String(t('sort_by', 'Sort By:'))}
                  </p>
                  <div className='relative'>
                    <select
                      value={sortBy}
                      onChange={(e) => setSortBy(e.target.value as SortOption)}
                      className='w-full h-10 rounded-md border border-solid border-sd-input bg-sd-input-bg px-3 pe-8 text-sm text-sd-foreground focus:outline-none focus:ring-1 focus:ring-sd-ring appearance-none cursor-pointer'
                    >
                      <option value='price'>{String(t('by_price', 'By Price'))}</option>
                      <option value='price-desc'>{String(t('by_price_desc', 'By Price (High → Low)'))}</option>
                      <option value='date'>{String(t('by_date', 'By Date'))}</option>
                      <option value='date-desc'>{String(t('by_date_desc', 'By Date (Latest first)'))}</option>
                      <option value='name'>{String(t('by_name', 'By Name'))}</option>
                      <option value='name-desc'>{String(t('by_name_desc', 'By Name (Z → A)'))}</option>
                    </select>
                    <ChevronDown className='pointer-events-none absolute end-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-sd-muted-foreground' strokeWidth={1.5} />
                  </div>
                </div>

                {/* Courier Filter */}
                {uniqueCouriers.length > 0 && (
                  <div>
                    <p className='text-xs font-semibold uppercase tracking-wider text-sd-muted-foreground mb-3'>
                      {String(t('courier', 'Courier'))}
                    </p>
                    <div className='flex flex-col gap-2.5'>
                      {uniqueCouriers.map(([slug, name]) => (
                        <label key={slug} className='flex items-center gap-2 text-sm cursor-pointer'>
                          <Checkbox
                            id={`courier-${slug}`}
                            checked={selectedCouriers.has(slug)}
                            onCheckedChange={() => toggleCourierFilter(slug)}
                            className='h-4 w-4 rounded-full border-sd-primary data-[state=checked]:bg-sd-primary data-[state=checked]:text-sd-primary-foreground'
                          />
                          {carrierDisplayName(slug, name)}
                        </label>
                      ))}
                    </div>
                  </div>
                )}
              </section>
            </div>

            {/* Right — Services Table */}
            <div className='flex flex-col gap-6'>
              <section className='rounded-2xl border border-sd-border bg-sd-card shadow-sm overflow-hidden'>
                <div className='px-5 py-4 border-b border-sd-border'>
                  <h2 className='text-sm font-bold uppercase tracking-wider text-sd-foreground'>
                    {String(t('services', 'Services'))}
                  </h2>
                </div>

                {/* Table Header */}
                <div className='grid grid-cols-[24px_1fr_1fr_100px_140px] items-center px-5 py-3 border-b border-sd-border text-xs font-semibold uppercase tracking-wider text-sd-muted-foreground'>
                  <span />
                  <span
                    className='flex items-center gap-1 cursor-pointer select-none hover:text-sd-foreground transition-colors'
                    onClick={() => setSortBy(sortBy === 'name' ? 'name-desc' : 'name')}
                  >
                    {String(t('name', 'Name'))}
                    {sortBy === 'name' && <ArrowUp className='h-3 w-3' strokeWidth={2} />}
                    {sortBy === 'name-desc' && <ArrowDown className='h-3 w-3' strokeWidth={2} />}
                  </span>
                  <span
                    className='flex items-center gap-1 cursor-pointer select-none hover:text-sd-foreground transition-colors'
                    onClick={() => setSortBy(sortBy === 'date' ? 'date-desc' : 'date')}
                  >
                    {String(t('delivery_date', 'Delivery Date'))}
                    {sortBy === 'date' && <ArrowUp className='h-3 w-3' strokeWidth={2} />}
                    {sortBy === 'date-desc' && <ArrowDown className='h-3 w-3' strokeWidth={2} />}
                  </span>
                  <span
                    className='flex items-center gap-1 cursor-pointer select-none hover:text-sd-foreground transition-colors'
                    onClick={() => setSortBy(sortBy === 'price' ? 'price-desc' : 'price')}
                  >
                    {String(t('price', 'Price'))}
                    {sortBy === 'price' && <ArrowUp className='h-3 w-3' strokeWidth={2} />}
                    {sortBy === 'price-desc' && <ArrowDown className='h-3 w-3' strokeWidth={2} />}
                  </span>
                  <span />
                </div>

                {/* All Carrier Rows (sorted together) */}
                {sorted.map((carrier) => {
                  const key = `${carrier.carrier_slug}-${carrier.delivery_day}`;
                  const isExpanded = expandedRows.has(key);
                  const isRecommended = carrier.carrier_slug === recommendedSlug;
                  return (
                    <div
                      key={key}
                      className='border-b border-sd-border last:border-b-0'
                    >
                      <div
                        className={cn(
                          'grid grid-cols-[24px_1fr_1fr_100px_140px] items-center px-5 py-4 transition-colors cursor-pointer',
                          selectedCarrier?.carrier_slug === carrier.carrier_slug && selectedCarrier?.delivery_day === carrier.delivery_day
                            ? 'bg-sd-primary/5'
                            : 'hover:bg-sd-muted/30',
                        )}
                        onClick={() => toggleRow(key)}
                      >
                        <ChevronDown
                          className={cn(
                            'h-4 w-4 text-sd-muted-foreground transition-transform',
                            isExpanded ? 'rotate-0' : (isRTL ? 'rotate-90' : '-rotate-90'),
                          )}
                          strokeWidth={1.5}
                        />
                        <div className='flex items-center gap-3'>
                          {carrier.carrier_logo_url ? (
                            <img
                              src={carrier.carrier_logo_url}
                              alt={carrier.carrier_name}
                              className='h-8 w-8 rounded-full shrink-0 object-contain'
                              onError={(e) => {
                                (e.target as HTMLImageElement).style.display = 'none';
                              }}
                            />
                          ) : (
                            <div className='h-8 w-8 rounded-full shrink-0 bg-sd-muted flex items-center justify-center text-xs font-bold text-sd-muted-foreground'>
                              {carrier.carrier_name.charAt(0).toUpperCase()}
                            </div>
                          )}
                          <div className='flex items-center gap-2'>
                            <span className='text-sm font-medium truncate'>{carrierDisplayName(carrier.carrier_slug, carrier.carrier_name)}</span>
                            {isRecommended && (
                              <span className='inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-semibold text-emerald-700'>
                                <Sparkles className='h-3 w-3' />
                                {String(t('recommended'))}
                              </span>
                            )}
                            {/* TEMP-9 — cold-chain capability pill. Renders
                                ONLY on explicit backend true; absent field
                                (pre-TEMP-8 backend) shows nothing. */}
                            {carrier.supports_temperature_control === true && (
                              <span
                                data-testid='carrier-temperature-pill'
                                className='inline-flex items-center gap-1 rounded-full bg-blue-100 px-2 py-0.5 text-[10px] font-semibold text-blue-700'
                              >
                                <Snowflake className='h-3 w-3' />
                                {String(t('temperature_controlled'))}
                              </span>
                            )}
                          </div>
                        </div>
                        <div>
                          <p className='text-sm font-medium'>{getDeliveryDateDisplay(carrier)}</p>
                          <p className='text-xs text-sd-muted-foreground'>
                            {getDeliveryTypeLabel(carrier)}
                          </p>
                        </div>
                        <span className='text-sm font-semibold tabular-nums' dir='ltr'>
                          {Math.round(carrier.shipping_fee).toLocaleString()} {String(t('iqd'))}
                        </span>
                        <div className='flex justify-end'>
                          {selectedCarrier?.carrier_slug === carrier.carrier_slug && selectedCarrier?.delivery_day === carrier.delivery_day ? (
                            <span className='w-28 h-9 rounded-md bg-sd-primary/10 border border-sd-primary px-3 text-sm font-medium text-sd-primary inline-flex items-center justify-center'>
                              {String(t('selected', 'Selected'))}
                            </span>
                          ) : (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleSelect(carrier);
                              }}
                              className='w-28 h-9 rounded-md border border-sd-border bg-sd-background px-3 text-sm font-medium text-sd-foreground hover:bg-sd-accent hover:text-sd-accent-foreground transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sd-ring focus-visible:ring-offset-2'
                            >
                              {String(t('select', 'Select'))}
                            </button>
                          )}
                        </div>
                      </div>

                      {/* Expanded Detail */}
                      {isExpanded && (
                        <div className='px-5 pb-4 pt-1 ps-[44px]'>
                          {getCutoffMessage(carrier) ? (
                            <div className='rounded-lg bg-blue-50 border border-blue-200 px-4 py-3 flex items-start gap-3'>
                              <Info className='h-4 w-4 text-blue-600 shrink-0 mt-0.5' strokeWidth={2} />
                              <p className='text-sm text-blue-800'>
                                {getCutoffMessage(carrier)}
                              </p>
                            </div>
                          ) : (
                            <div className='rounded-lg bg-blue-50 border border-blue-200 px-4 py-3 flex items-start gap-3'>
                              <Info className='h-4 w-4 text-blue-600 shrink-0 mt-0.5' strokeWidth={2} />
                              <p className='text-sm text-blue-800'>
                                {carrier.delivery_day === 'same'
                                  ? String(t('same_day_delivery_info', 'This carrier provides same-day delivery for your route.'))
                                  : String(t('next_day_delivery_info', 'This carrier provides next-day delivery for your route.'))}
                              </p>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}

                {/* Empty State */}
                {sorted.length === 0 && !isLoading && (
                  <div className='px-5 py-12 text-center'>
                    <AlertCircle className='h-8 w-8 text-sd-muted-foreground mx-auto mb-3' />
                    <p className='text-sm text-sd-muted-foreground'>
                      {String(t('no_carriers_match_filter'))}
                    </p>
                    {recommendedSlug && (
                      <p className='text-sm text-emerald-600 font-medium mt-2'>
                        {String(t('enable_carriers_recommendation', { carrier: titleCase(carriers.find((c) => c.carrier_slug === recommendedSlug)?.carrier_name || recommendedSlug) }))}
                      </p>
                    )}
                  </div>
                )}
              </section>

              {/* Recommended badge is shown inline in the carrier table rows */}
            </div>
          </div>
        )}
      </div>

      {/* Footer */}
      <div className='absolute bottom-4 start-0 end-0 z-30 px-4 sm:px-8'>
        <div
          className={cn(
            'rounded-2xl border border-sd-border bg-sd-card px-5 py-4 flex items-center justify-between transition-shadow',
            isScrolledUp && 'shadow-lg',
          )}
        >
          <Link
            to={isEditMode && editOrderUid ? `/orders/edit/${editOrderUid}` : ROUTE.ADDNEWORDER}
            className='inline-flex justify-center whitespace-nowrap items-center gap-2 h-10 rounded-md border border-sd-border bg-transparent px-4 text-sm font-medium uppercase text-sd-foreground hover:bg-sd-accent hover:text-sd-accent-foreground transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sd-ring focus-visible:ring-offset-2'
          >
            <ArrowLeft className={cn('h-4 w-4', isRTL && 'rotate-180')} strokeWidth={2} />
            {String(t('back', 'BACK'))}
          </Link>
          <button
            onClick={() => selectedCarrier && navigate(ROUTE.ORDER_SUMMARY)}
            disabled={!selectedCarrier}
            className={cn(
              'inline-flex justify-center whitespace-nowrap items-center gap-2 h-10 rounded-md px-6 text-sm font-medium uppercase transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sd-ring focus-visible:ring-offset-2',
              selectedCarrier
                ? 'bg-sd-primary text-sd-primary-foreground hover:opacity-90'
                : 'bg-sd-muted text-sd-muted-foreground cursor-not-allowed',
            )}
          >
            {String(t('continue', 'CONTINUE'))}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SelectCarrier;
